import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type {
  Application,
  Capability,
  Policy,
  PolicyDefinition,
  SafetyIssue,
  WifiNetwork,
} from '../lib/api';
import { ErrorNotice, Loading, Notice, Panel, formatDateTime, useAsync } from '../components/ui';

/**
 * The policy editor.
 *
 * Two rules govern what appears here:
 *
 *  1. Every control on this page maps to a real backend field AND a real
 *     DevicePolicyManager call in the agent. Nothing is rendered that the
 *     system cannot actually enforce.
 *  2. Every control shows the Android API level it genuinely requires, and
 *     says plainly when the platform WITHDREW it above a version — Android
 *     removes capability from a Device Owner as well as adding it.
 */

const EMPTY: PolicyDefinition = {
  schemaVersion: 1,
  kiosk: { enabled: false, kioskPackage: undefined, replaceHomeLauncher: true, allowedSystemFeatures: [] },
  applications: {
    allowedPackages: [],
    blockUninstallManaged: true,
    hideUnlistedApps: false,
    blockFileManagers: false,
    autoGrantPermissions: false,
    expectedSignatures: {},
  },
  screen: {
    statusBarDisabled: false,
    keyguardDisabled: false,
    screenCaptureDisabled: false,
    keepScreenOnWhileCharging: false,
  },
  camera: { disabled: false },
  password: {
    enabled: false,
    quality: 'NONE',
    minimumLength: 0,
    requiredComplexity: 'NONE',
    maximumFailedPasswordsForWipe: 0,
    expirationDays: 0,
    historyLength: 0,
  },
  factoryResetProtection: { enabled: false, accounts: [] },
  restrictions: {},
  wifi: { networks: [] },
  checkInIntervalSeconds: 60,
};

/**
 * How the restriction checkboxes are grouped.
 *
 * The list comes from the server's capability catalogue, so it can never drift
 * from what the agent implements; this only decides which heading each one sits
 * under. A restriction the server adds and this map does not know about falls
 * into "Other restrictions" rather than disappearing.
 */
const RESTRICTION_GROUPS: Array<{ title: string; hint: string; keys: string[] }> = [
  {
    title: 'Installation and applications',
    hint: 'Closes the routes an unapproved application reaches the tablet through.',
    keys: [
      'disallowInstallApps',
      'disallowUninstallApps',
      'disallowInstallUnknownSources',
      'disallowInstallUnknownSourcesGlobally',
      'disallowAppsControl',
    ],
  },
  {
    title: 'Users and accounts',
    hint: 'Stops a second identity being used to escape the exam profile.',
    keys: ['disallowAddUser', 'disallowModifyAccounts', 'disallowConfigCredentials'],
  },
  {
    title: 'Storage and USB',
    hint: 'Physical routes for getting files on or off the device.',
    keys: ['disallowUsbFileTransfer', 'disallowMountPhysicalMedia', 'disallowDebuggingFeatures'],
  },
  {
    title: 'System and recovery',
    hint: 'Settings paths a student could use to unwind the policy.',
    keys: ['disallowFactoryReset', 'disallowSafeBoot', 'disallowConfigDateTime'],
  },
  {
    title: 'Network and radios',
    hint: 'Read the Wi-Fi warning before enabling the first of these.',
    keys: ['disallowConfigWifi', 'disallowConfigBluetooth', 'disallowConfigVpn', 'disallowAirplaneMode'],
  },
  {
    title: 'Display and telephony',
    hint: 'Lower-risk controls that mostly reduce distraction.',
    keys: [
      'disallowConfigBrightness',
      'disallowConfigScreenTimeout',
      'disallowSms',
      'disallowOutgoingCalls',
    ],
  },
];

const LOCK_TASK_FEATURES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'SYSTEM_INFO', label: 'Status bar system info', hint: 'Clock, battery and signal stay visible.' },
  { value: 'NOTIFICATIONS', label: 'Notifications', hint: 'Notifications and the shade remain available.' },
  { value: 'HOME', label: 'Home button', hint: 'Home works — normally leave OFF for an exam.' },
  { value: 'OVERVIEW', label: 'Recent apps', hint: 'Recents works — normally leave OFF for an exam.' },
  { value: 'GLOBAL_ACTIONS', label: 'Power menu', hint: 'Long-press power shows the power menu.' },
  { value: 'KEYGUARD', label: 'Lock screen', hint: 'The keyguard is allowed inside the kiosk.' },
];

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'test' | 'production'>('test');
  const [definition, setDefinition] = useState<PolicyDefinition>(EMPTY);
  const [issues, setIssues] = useState<{ errors: SafetyIssue[]; warnings: SafetyIssue[] } | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const existing = useAsync(
    (signal) => (isNew ? Promise.resolve(null) : api.get<{ policy: Policy }>(`/policies/${id}`, signal)),
    [id],
  );
  const capabilities = useAsync(
    (signal) => api.get<{ capabilities: Capability[] }>('/policies/capabilities', signal),
    [],
  );
  const apps = useAsync((signal) => api.get<{ applications: Application[] }>('/applications', signal), []);

  useEffect(() => {
    const policy = existing.data?.policy;
    if (!policy) return;
    setName(policy.name);
    setDescription(policy.description);
    setKind(policy.kind);
    // Merged section by section, not with a single spread: a policy saved
    // before a section existed has no key for it, and a shallow spread would
    // replace the whole section with undefined rather than keeping the default.
    setDefinition({
      ...EMPTY,
      ...policy.definition,
      kiosk: { ...EMPTY.kiosk, ...(policy.definition.kiosk ?? {}) },
      applications: { ...EMPTY.applications, ...(policy.definition.applications ?? {}) },
      screen: { ...EMPTY.screen, ...(policy.definition.screen ?? {}) },
      camera: { ...EMPTY.camera, ...(policy.definition.camera ?? {}) },
      password: { ...EMPTY.password, ...(policy.definition.password ?? {}) },
      factoryResetProtection: {
        ...EMPTY.factoryResetProtection,
        ...(policy.definition.factoryResetProtection ?? {}),
      },
      restrictions: policy.definition.restrictions ?? {},
      wifi: { networks: policy.definition.wifi?.networks ?? [] },
    });
  }, [existing.data]);

  // Live validation while editing, so safety errors surface before saving.
  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .post<{ errors: SafetyIssue[]; warnings: SafetyIssue[] }>('/policies/validate', { definition })
        .then(setIssues)
        .catch(() => setIssues(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [definition]);

  const setRestriction = (key: string, value: boolean) =>
    setDefinition((d) => ({ ...d, restrictions: { ...d.restrictions, [key]: value } }));

  const patch = <K extends keyof PolicyDefinition>(
    section: K,
    values: Partial<PolicyDefinition[K]>,
  ) =>
    setDefinition((d) => ({ ...d, [section]: { ...(d[section] as object), ...values } }));

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    setMessage(null);
    try {
      const body = { name, description, kind, definition };
      if (isNew) {
        const created = await api.post<{ policy: Policy }>('/policies', body);
        navigate(`/policies/${created.policy.id}`, { replace: true });
        setMessage('Policy created.');
      } else {
        const updated = await api.put<{ message: string }>(`/policies/${id}`, body);
        setMessage(updated.message);
        existing.reload();
      }
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  const markValidated = async () => {
    if (!window.confirm(
      'Mark this policy as proven on real hardware? Only do this after a device running it ' +
      'has been confirmed still checking in. This releases it for fleet-wide assignment.',
    )) return;
    try {
      await api.post(`/policies/${id}/validated`);
      existing.reload();
      setMessage('Policy marked validated. It can now be assigned to multiple devices.');
    } catch (err) {
      setSaveError(err);
    }
  };

  if (!isNew && existing.loading && !existing.data) return <Loading what="policy" />;

  const allCapabilities = capabilities.data?.capabilities ?? [];
  const capabilityFor = (key: string) => allCapabilities.find((c) => c.key === key);
  const restrictionCapabilities = allCapabilities.filter((c) => c.key.startsWith('restrictions.'));
  const grouped = new Set(RESTRICTION_GROUPS.flatMap((g) => g.keys));
  const ungrouped = restrictionCapabilities.filter(
    (c) => !grouped.has(c.key.replace('restrictions.', '')),
  );
  const policy = existing.data?.policy;

  // Mirrors the server rule so the administrator sees the problem while
  // ticking the box rather than on save. The backend remains authoritative —
  // this only shortens the feedback loop.
  const notificationsWithoutHome =
    definition.kiosk.allowedSystemFeatures.includes('NOTIFICATIONS') &&
    !definition.kiosk.allowedSystemFeatures.includes('HOME');

  const restrictionCheckbox = (capability: Capability) => {
    const key = capability.key.replace('restrictions.', '');
    return (
      <ApiLabel key={capability.key} capability={capability}>
        <input
          type="checkbox"
          checked={Boolean(definition.restrictions[key])}
          onChange={(e) => setRestriction(key, e.target.checked)}
        />
      </ApiLabel>
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{isNew ? 'New policy' : policy?.name}</h2>
          <p>
            {isNew ? 'Define what devices running this policy may do.' : `Version ${policy?.version}`} ·{' '}
            <Link to="/policies">back to policies</Link>
          </p>
        </div>
        <div className="actions">
          {!isNew && policy && !policy.validated_at && (
            <button onClick={markValidated}>Mark validated on hardware</button>
          )}
          <button className="primary" onClick={save} disabled={busy || !name || (issues?.errors.length ?? 0) > 0}>
            {busy ? 'Saving…' : isNew ? 'Create policy' : 'Save changes'}
          </button>
        </div>
      </div>

      {message && <Notice kind="success">{message}</Notice>}
      <ErrorNotice error={saveError} />

      {issues && issues.errors.length > 0 && (
        <Notice kind="error">
          <strong>This policy cannot be saved — it could make devices unmanageable.</strong>
          <ul>{issues.errors.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
        </Notice>
      )}
      {issues && issues.warnings.length > 0 && (
        <Notice kind="warning">
          <strong>Consequences to be aware of</strong>
          <ul>{issues.warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
        </Notice>
      )}

      <div className="grid cols-2">
        {/* ---------------------------------------------------------------- */}
        <Panel title="Identity">
          <label>
            <span>Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <span>Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label>
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'test' | 'production')}>
              <option value="test">Test — permissive, recoverable</option>
              <option value="production">Production — strict, fleet-guarded</option>
            </select>
          </label>
          <label>
            <span>Check-in interval (seconds)</span>
            <input
              type="number"
              min={30}
              max={86400}
              value={definition.checkInIntervalSeconds}
              onChange={(e) => setDefinition((d) => ({ ...d, checkInIntervalSeconds: Number(e.target.value) }))}
            />
          </label>
          <p className="small muted">
            Also the heartbeat interval. A device that misses this by five intervals is
            classified offline and analysed for a likely cause.
          </p>
          {!isNew && policy && (
            <p className="small muted">
              {policy.validated_at
                ? `Validated on hardware ${formatDateTime(policy.validated_at)}.`
                : 'Not yet validated on hardware. Saving any change resets validation.'}
            </p>
          )}
        </Panel>

        {/* ---------------------------------------------------------------- */}
        <Panel title="1 · Kiosk / exam mode">
          <ApiLabel capability={capabilityFor('kiosk.enabled')} strong>
            <input
              type="checkbox"
              checked={definition.kiosk.enabled}
              onChange={(e) => patch('kiosk', { enabled: e.target.checked })}
            />
          </ApiLabel>

          <label>
            <span>Kiosk application</span>
            <select
              value={definition.kiosk.kioskPackage ?? ''}
              onChange={(e) => {
                const pkg = e.target.value || undefined;
                setDefinition((d) => ({
                  ...d,
                  kiosk: { ...d.kiosk, kioskPackage: pkg },
                  // The kiosk app must be in the allow-list or lock task refuses
                  // to start it, so keep them consistent automatically.
                  applications: {
                    ...d.applications,
                    allowedPackages: pkg
                      ? [...new Set([...d.applications.allowedPackages, pkg])]
                      : d.applications.allowedPackages,
                  },
                }));
              }}
            >
              <option value="">Select an uploaded application…</option>
              {(apps.data?.applications ?? []).map((a) => (
                <option key={a.id} value={a.package_name}>
                  {a.display_name} ({a.package_name})
                </option>
              ))}
            </select>
          </label>
          {(apps.data?.applications.length ?? 0) === 0 && (
            <p className="small muted" style={{ marginTop: -6 }}>
              No applications uploaded yet. <Link to="/applications">Upload an APK</Link> first.
            </p>
          )}

          <ApiLabel capability={capabilityFor('kiosk.homeLauncher')}>
            <input
              type="checkbox"
              checked={definition.kiosk.replaceHomeLauncher}
              onChange={(e) => patch('kiosk', { replaceHomeLauncher: e.target.checked })}
            />
          </ApiLabel>
          <p className="small muted" style={{ marginTop: -4 }}>
            With this off, pressing Home leaves the kiosk shell and a reboot does not return to
            the exam application. Lock task still pins the app while it is running.
          </p>

          <h4 style={{ marginBottom: 6 }}>
            System UI allowed inside the kiosk
            <span className="badge warn" style={{ fontSize: 11, marginLeft: 6 }}>API 28+</span>
          </h4>
          <p className="small muted" style={{ marginTop: 0 }}>
            Everything unticked is the strictest setting. On Android 7 and 8 lock task suppresses
            Home and Recents wholesale and these cannot be relaxed — the device reports that
            back rather than silently ignoring it.
          </p>
          {notificationsWithoutHome && (
            <Notice kind="error">
              <strong>Android will reject this combination.</strong>
              <div>
                <code>setLockTaskFeatures</code> throws when notifications are allowed inside
                lock task without the Home button also being allowed. Either allow Home as well,
                or turn notifications off — for an exam device, off is usually what you want.
              </div>
            </Notice>
          )}
          {LOCK_TASK_FEATURES.map((f) => (
            <label className="inline" key={f.value}>
              <input
                type="checkbox"
                checked={definition.kiosk.allowedSystemFeatures.includes(f.value)}
                onChange={(e) =>
                  patch('kiosk', {
                    allowedSystemFeatures: e.target.checked
                      ? [...definition.kiosk.allowedSystemFeatures, f.value]
                      : definition.kiosk.allowedSystemFeatures.filter((v) => v !== f.value),
                  })
                }
              />
              <span>
                Allow {f.label}
                <div className="small muted">{f.hint}</div>
              </span>
            </label>
          ))}
        </Panel>
      </div>

      {/* ------------------------------------------------------------------ */}
      <Panel title="2 · Applications">
        <div className="grid cols-2">
          <div>
            <ApiLabel capability={capabilityFor('applications.blockUninstall')}>
              <input
                type="checkbox"
                checked={definition.applications.blockUninstallManaged}
                onChange={(e) => patch('applications', { blockUninstallManaged: e.target.checked })}
              />
            </ApiLabel>
            <ApiLabel capability={capabilityFor('applications.hideUnlisted')}>
              <input
                type="checkbox"
                checked={definition.applications.hideUnlistedApps}
                onChange={(e) => patch('applications', { hideUnlistedApps: e.target.checked })}
              />
            </ApiLabel>
            <ApiLabel capability={capabilityFor('applications.autoGrantPermissions')}>
              <input
                type="checkbox"
                checked={definition.applications.autoGrantPermissions}
                onChange={(e) => patch('applications', { autoGrantPermissions: e.target.checked })}
              />
            </ApiLabel>
          </div>

          <div>
            <ApiLabel capability={capabilityFor('applications.blockFileManagers')} strong>
              <input
                type="checkbox"
                checked={definition.applications.blockFileManagers}
                onChange={(e) => patch('applications', { blockFileManagers: e.target.checked })}
              />
            </ApiLabel>
            <p className="small muted" style={{ marginTop: -4 }}>
              Hides the stock file managers <em>and</em> redirects the system document-picker
              intents (<code>GET_CONTENT</code>, <code>OPEN_DOCUMENT</code>,{' '}
              <code>OPEN_DOCUMENT_TREE</code>) so an application cannot reach a file through a
              picker either. Hiding launcher icons alone never closed that route.
              <strong> Pair it with the installation restrictions below</strong> — this blocks
              discovery, not the ability to install.
            </p>
          </div>
        </div>

        <h4 style={{ marginBottom: 6 }}>Allowed packages</h4>
        <p className="small muted" style={{ marginTop: 0 }}>
          Applications permitted to run and to be pinned in lock task. The kiosk application is
          added automatically. When "hide applications not on the allow list" is on, everything
          else with a launcher icon is hidden.
        </p>
        <PackageListEditor
          value={definition.applications.allowedPackages}
          options={(apps.data?.applications ?? []).map((a) => a.package_name)}
          onChange={(allowedPackages) => patch('applications', { allowedPackages })}
        />

        <h4 style={{ marginBottom: 6 }}>Pinned signing certificates</h4>
        <p className="small muted" style={{ marginTop: 0 }}>
          The device reports on every check-in whether these packages are still signed by the
          expected certificate. A package name and version can both be forged by rebuilding an
          app; the signing certificate cannot. A mismatch is raised as a critical audit event and
          is never uninstalled automatically — a false positive mid-exam would be worse than the
          tamper it guards against.
        </p>
        <SignaturePinEditor
          value={definition.applications.expectedSignatures}
          applications={apps.data?.applications ?? []}
          onChange={(expectedSignatures) => patch('applications', { expectedSignatures })}
        />
      </Panel>

      {/* ------------------------------------------------------------------ */}
      <Panel title="3 · Security">
        <div className="grid cols-2">
          <div>
            <h4 style={{ marginTop: 0, marginBottom: 6 }}>Camera and capture</h4>
            <ApiLabel capability={capabilityFor('camera.disabled')} strong>
              <input
                type="checkbox"
                checked={definition.camera.disabled}
                onChange={(e) => patch('camera', { disabled: e.target.checked })}
              />
            </ApiLabel>
            <ApiLabel capability={capabilityFor('screen.screenCaptureDisabled')}>
              <input
                type="checkbox"
                checked={definition.screen.screenCaptureDisabled}
                onChange={(e) => patch('screen', { screenCaptureDisabled: e.target.checked })}
              />
            </ApiLabel>

            <h4 style={{ marginBottom: 6 }}>Screen</h4>
            <ApiLabel capability={capabilityFor('screen.statusBarDisabled')}>
              <input
                type="checkbox"
                checked={definition.screen.statusBarDisabled}
                onChange={(e) => patch('screen', { statusBarDisabled: e.target.checked })}
              />
            </ApiLabel>
            <ApiLabel capability={capabilityFor('screen.keyguardDisabled')}>
              <input
                type="checkbox"
                checked={definition.screen.keyguardDisabled}
                onChange={(e) => patch('screen', { keyguardDisabled: e.target.checked })}
              />
            </ApiLabel>
            <ApiLabel capability={capabilityFor('screen.keepScreenOn')}>
              <input
                type="checkbox"
                checked={definition.screen.keepScreenOnWhileCharging}
                onChange={(e) => patch('screen', { keepScreenOnWhileCharging: e.target.checked })}
              />
            </ApiLabel>
          </div>

          <div>
            <h4 style={{ marginTop: 0, marginBottom: 6 }}>Factory Reset Protection</h4>
            <ApiLabel capability={capabilityFor('factoryResetProtection')}>
              <input
                type="checkbox"
                checked={definition.factoryResetProtection.enabled}
                onChange={(e) => patch('factoryResetProtection', { enabled: e.target.checked })}
              />
            </ApiLabel>
            <p className="small muted" style={{ marginTop: -4 }}>
              FRP protects a device <em>after</em> an unauthorised reset — it does not prevent the
              reset, and no Device Owner application can. Behaviour above Android 11 is
              substantially the manufacturer's, so the agent sets the policy, reads it back, and
              reports FAILED if the device did not actually enable it.
            </p>
            <label>
              <span>Authorised Google accounts (one per line)</span>
              <textarea
                rows={3}
                value={definition.factoryResetProtection.accounts.join('\n')}
                placeholder="it-admin@institution.edu"
                onChange={(e) =>
                  patch('factoryResetProtection', {
                    accounts: e.target.value
                      .split('\n')
                      .map((a) => a.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
            <p className="small muted" style={{ marginTop: -6 }}>
              After any factory reset, including a recovery-mode wipe, setup cannot be completed
              without one of these accounts.
            </p>
          </div>
        </div>
      </Panel>

      {/* ------------------------------------------------------------------ */}
      <Panel title="4 · Device password">
        <ApiLabel
          capability={{
            key: 'password.enabled',
            label: 'Manage the device unlock credential',
            status: 'supported',
            minApiLevel: 21,
            androidMechanism: 'DevicePolicyManager password policy family',
          }}
          strong
        >
          <input
            type="checkbox"
            checked={definition.password.enabled}
            onChange={(e) => patch('password', { enabled: e.target.checked })}
          />
        </ApiLabel>
        <p className="small muted" style={{ marginTop: -4 }}>
          Leave off unless the tablets genuinely need a lock credential. When off, the agent does
          not touch password policy at all, rather than clearing settings another administrator
          may have set.
        </p>

        {definition.password.enabled && (
          <>
            <Notice kind="info">
              <strong>Android splits this family at API 31.</strong> On Android 11 and below the
              agent uses <code>setPasswordQuality</code> with a minimum length. On Android 12 and
              above those calls <em>throw</em> for a Device Owner, so the agent uses{' '}
              <code>setRequiredPasswordComplexity</code> instead. Set both; each device uses the
              one it supports and reports the other as unsupported rather than pretending.
            </Notice>

            <div className="grid cols-2">
              <div>
                <h4 style={{ marginTop: 0, marginBottom: 6 }}>
                  Android 12+{' '}
                  <span className="badge muted" style={{ fontSize: 11 }}>API 31+</span>
                </h4>
                <label>
                  <span>Required complexity</span>
                  <select
                    value={definition.password.requiredComplexity}
                    onChange={(e) =>
                      patch('password', {
                        requiredComplexity: e.target.value as PolicyDefinition['password']['requiredComplexity'],
                      })
                    }
                  >
                    <option value="NONE">None — no credential required</option>
                    <option value="LOW">Low — pattern or any PIN</option>
                    <option value="MEDIUM">Medium — PIN with no repeats, or short alphabetic</option>
                    <option value="HIGH">High — long PIN or alphanumeric</option>
                  </select>
                </label>
                <p className="small muted" style={{ marginTop: -6 }}>
                  The device chooses the concrete rules for each level, which is why Android
                  replaced the old digit-and-symbol counting.
                </p>
              </div>

              <div>
                <h4 style={{ marginTop: 0, marginBottom: 6 }}>
                  Android 11 and below{' '}
                  <span className="badge muted" style={{ fontSize: 11 }}>API 21–30</span>
                </h4>
                <label>
                  <span>Password quality</span>
                  <select
                    value={definition.password.quality}
                    onChange={(e) =>
                      patch('password', {
                        quality: e.target.value as PolicyDefinition['password']['quality'],
                      })
                    }
                  >
                    <option value="NONE">Unspecified</option>
                    <option value="SOMETHING">Something — any credential</option>
                    <option value="NUMERIC">Numeric</option>
                    <option value="NUMERIC_COMPLEX">Numeric, no repeats or sequences</option>
                    <option value="ALPHABETIC">Alphabetic</option>
                    <option value="ALPHANUMERIC">Alphanumeric</option>
                    <option value="COMPLEX">Complex</option>
                  </select>
                </label>
                <label>
                  <span>Minimum length (0 = platform default)</span>
                  <input
                    type="number"
                    min={0}
                    max={16}
                    value={definition.password.minimumLength}
                    onChange={(e) => patch('password', { minimumLength: Number(e.target.value) })}
                  />
                </label>
              </div>
            </div>

            <div className="grid cols-2">
              <label>
                <span>Password expiry (days, 0 = never)</span>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={definition.password.expirationDays}
                  onChange={(e) => patch('password', { expirationDays: Number(e.target.value) })}
                />
              </label>
              <label>
                <span>Password history length (0 = none)</span>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={definition.password.historyLength}
                  onChange={(e) => patch('password', { historyLength: Number(e.target.value) })}
                />
              </label>
            </div>

            <Notice kind="warning">
              <strong>Wipe after failed unlock attempts — this destroys data.</strong>
              <div>
                Android factory resets the tablet when the limit is reached. There is no
                confirmation, no delay and no recovery: everything on the device is gone. Leave
                at 0 unless the threat model genuinely justifies it. Values below 3 are rejected,
                because an ordinary mistyped PIN would reach them.
              </div>
            </Notice>
            <label>
              <span>Wipe after N failed unlock attempts (0 = never)</span>
              <input
                type="number"
                min={0}
                max={100}
                value={definition.password.maximumFailedPasswordsForWipe}
                onChange={(e) =>
                  patch('password', { maximumFailedPasswordsForWipe: Number(e.target.value) })
                }
              />
            </label>
          </>
        )}
      </Panel>

      {/* ------------------------------------------------------------------ */}
      <Panel title="5 · Device restrictions">
        <p className="small muted" style={{ marginTop: 0 }}>
          Each restriction lists the Android API level it genuinely requires. Anything above a
          device's level is reported as UNSUPPORTED on that device rather than silently ignored,
          and the agent reads every restriction back after setting it — one the platform accepted
          and dropped shows as FAILED.
          <strong> API 24 is Android 7.0.</strong>
        </p>

        {RESTRICTION_GROUPS.map((group) => {
          const caps = group.keys
            .map((k) => restrictionCapabilities.find((c) => c.key === `restrictions.${k}`))
            .filter((c): c is Capability => Boolean(c));
          if (caps.length === 0) return null;
          return (
            <div key={group.title} style={{ marginBottom: 18 }}>
              <h4 style={{ marginBottom: 2 }}>{group.title}</h4>
              <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>{group.hint}</p>
              <div className="grid cols-2">{caps.map(restrictionCheckbox)}</div>
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 8 }}>Other restrictions</h4>
            <div className="grid cols-2">{ungrouped.map(restrictionCheckbox)}</div>
          </div>
        )}
      </Panel>

      {/* ------------------------------------------------------------------ */}
      <Panel title="6 · Network">
        <Notice kind="warning">
          <strong>Never block Wi-Fi configuration without provisioning a network.</strong>
          <div>
            Doing so removes every saved network and leaves the device permanently offline — it
            could not fetch a corrective policy, because that requires the network it just lost.
            The backend rejects such a policy outright, and the agent refuses it a second time on
            the device.
          </div>
        </Notice>

        <WifiEditor
          networks={definition.wifi.networks}
          onChange={(networks) => setDefinition((d) => ({ ...d, wifi: { networks } }))}
        />

        <Notice kind="info">
          <strong>Runtime Wi-Fi provisioning works on Android 9 and below only.</strong>
          <div>
            Android 10 removed <code>WifiManager.addNetwork</code> for this use. On Android 10+
            the agent reports <code>wifi.configureNetworks</code> as UNSUPPORTED rather than
            failing quietly — provision the network in the enrolment QR code instead, using the{' '}
            <code>PROVISIONING_WIFI_*</code> server settings.
          </div>
        </Notice>
        <p className="small muted" style={{ marginBottom: 0 }}>
          Passwords are never stored in the policy document or sent to the browser. A network
          references a server-side secret by name, and the value is injected at delivery time over
          the device's authenticated channel.
        </p>
      </Panel>
    </>
  );
}

/**
 * A checkbox labelled with what Android actually does, and at which API level.
 *
 * Rendering the mechanism next to the control is deliberate: it is the fastest
 * way for an administrator to check that the dashboard is not offering
 * something the platform cannot deliver.
 */
function ApiLabel({
  capability,
  children,
  strong,
}: {
  capability: Capability | undefined;
  children: React.ReactNode;
  strong?: boolean;
}) {
  if (!capability) return null;
  const tooNew = capability.minApiLevel > 24;
  const withdrawn = capability.maxApiLevel !== undefined;

  return (
    <label className="inline">
      {children}
      <span>
        {strong ? <strong>{capability.label}</strong> : capability.label}{' '}
        <span className={`badge ${tooNew || withdrawn ? 'warn' : 'muted'}`} style={{ fontSize: 11 }}>
          {withdrawn
            ? `API ${capability.minApiLevel}–${capability.maxApiLevel}`
            : `API ${capability.minApiLevel}`}
          {tooNew ? ' — not on Android 7' : ''}
          {withdrawn ? ' — withdrawn above' : ''}
        </span>
        {capability.destructive && (
          <span className="badge danger" style={{ fontSize: 11, marginLeft: 4 }}>DESTRUCTIVE</span>
        )}
        <div className="small muted mono">{capability.androidMechanism}</div>
        {(capability.reason ?? capability.note) && (
          <div className="small" style={{ color: 'var(--warn)' }}>
            {capability.reason ?? capability.note}
          </div>
        )}
      </span>
    </label>
  );
}

/** Add/remove package names, with the uploaded applications as suggestions. */
function PackageListEditor({
  value,
  options,
  onChange,
}: {
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const pkg = draft.trim();
    if (!pkg || value.includes(pkg)) return;
    onChange([...value, pkg]);
    setDraft('');
  };

  return (
    <div>
      <div className="actions" style={{ marginBottom: 8 }}>
        <input
          type="text"
          list="package-options"
          placeholder="com.example.app"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <datalist id="package-options">
          {options.map((o) => <option key={o} value={o} />)}
        </datalist>
        <button type="button" onClick={add}>Add package</button>
      </div>
      {value.length === 0 ? (
        <p className="small muted">
          No allow list. Every application is permitted to run, and hiding unlisted applications
          would hide everything with a launcher icon.
        </p>
      ) : (
        <ul className="small mono" style={{ paddingLeft: 18 }}>
          {value.map((pkg) => (
            <li key={pkg}>
              {pkg}{' '}
              <button
                type="button"
                className="link"
                onClick={() => onChange(value.filter((p) => p !== pkg))}
              >
                remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** packageName -> expected signing-certificate SHA-256. */
function SignaturePinEditor({
  value,
  applications,
  onChange,
}: {
  value: Record<string, string>;
  applications: Application[];
  onChange: (next: Record<string, string>) => void;
}) {
  const entries = Object.entries(value);
  const unpinned = applications.filter(
    (a) => !(a.package_name in value) && a.signing_cert_sha256,
  );

  return (
    <div>
      {entries.length === 0 && (
        <p className="small muted">
          No signing certificates pinned. Tamper detection is off for this policy.
        </p>
      )}
      <ul className="small mono" style={{ paddingLeft: 18 }}>
        {entries.map(([pkg, digest]) => (
          <li key={pkg} style={{ wordBreak: 'break-all' }}>
            {pkg} — {digest.slice(0, 24)}…{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                const next = { ...value };
                delete next[pkg];
                onChange(next);
              }}
            >
              unpin
            </button>
          </li>
        ))}
      </ul>
      {unpinned.length > 0 && (
        <div className="actions">
          {unpinned.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onChange({ ...value, [a.package_name]: a.signing_cert_sha256! })}
            >
              Pin {a.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Wi-Fi networks provisioned to the device.
 *
 * The password field is deliberately a REFERENCE, not a value: the policy
 * document is stored in the database and returned to the browser, and a real
 * pre-shared key must never be in either.
 */
function WifiEditor({
  networks,
  onChange,
}: {
  networks: WifiNetwork[];
  onChange: (next: WifiNetwork[]) => void;
}) {
  const update = (index: number, patch: Partial<WifiNetwork>) =>
    onChange(networks.map((n, i) => (i === index ? { ...n, ...patch } : n)));

  return (
    <div>
      {networks.length === 0 && (
        <p className="small muted">
          No networks provisioned by this policy. The device keeps whatever networks it already
          has, which is why blocking Wi-Fi configuration is refused in this state.
        </p>
      )}

      {networks.map((network, index) => (
        <div key={index} className="grid cols-2" style={{ marginBottom: 10 }}>
          <label>
            <span>SSID</span>
            <input
              type="text"
              value={network.ssid}
              onChange={(e) => update(index, { ssid: e.target.value })}
            />
          </label>
          <label>
            <span>Security</span>
            <select
              value={network.securityType}
              onChange={(e) =>
                update(index, { securityType: e.target.value as WifiNetwork['securityType'] })
              }
            >
              <option value="NONE">Open</option>
              <option value="WPA">WPA / WPA2</option>
              <option value="WEP">WEP</option>
            </select>
          </label>
          <label>
            <span>Password reference (server-side secret name)</span>
            <input
              type="text"
              placeholder="PROVISIONING_WIFI_PASSWORD"
              value={network.passwordRef ?? ''}
              onChange={(e) => update(index, { passwordRef: e.target.value || undefined })}
            />
          </label>
          <div>
            <label className="inline">
              <input
                type="checkbox"
                checked={network.hidden}
                onChange={(e) => update(index, { hidden: e.target.checked })}
              />
              <span>Hidden SSID</span>
            </label>
            <label className="inline">
              <input
                type="checkbox"
                checked={network.autoConnect}
                onChange={(e) => update(index, { autoConnect: e.target.checked })}
              />
              <span>Connect automatically</span>
            </label>
            <button type="button" onClick={() => onChange(networks.filter((_, i) => i !== index))}>
              Remove network
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          onChange([
            ...networks,
            { ssid: '', securityType: 'WPA', passwordRef: 'PROVISIONING_WIFI_PASSWORD', hidden: false, autoConnect: true },
          ])
        }
      >
        Add Wi-Fi network
      </button>
    </div>
  );
}
